import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { FilesSelectorButton } from 'components/FilesSelectorButton';
import type { ITabItem } from 'components/Form/ControlledTab';
import type {
    ITableLikeTreeContext,
    ITableLikeTreeItemNotification,
    ITableLikeTreeMenuOperationContext,
} from 'components/TableLikeTree/context';
import { TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import { TotalCount } from 'components/TotalCount';
import { UploadStatus } from 'components/UploadStatus';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimContractObjectData } from 'gql/graphql';
import {
    BimContractObjectStatusEnum,
    BimContractStatusEnum,
    ProjectContractTypeEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { BimCreateStructureObject } from 'pages/Project/BimProject/Contract/BimCreateStructureObject';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import type { SelectedFilters, TableColumn } from 'uikit';
import { Badge, Button, Informer, Layout, Loader, Tabs, cnMixSpace } from 'uikit';
import { isNumber, sortByYear } from 'utils';
import { runOnRefExists } from 'utils/runOnRefExists';

import cn from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useGetBimWorkOrderDataFromProject } from '../../WorkOrder/hooks';
import { BimCreateStructureSubObject } from '../BimCreateStructureSubObject';
import { useBimContract } from '../hooks/useBimContract';

import { useBimContractObjects } from './hooks/useBimContractObjects';
import { useBimContractStructureFilters } from './hooks/useBimContractStructureFilters';
import { useBimDeleteContractObject } from './hooks/useBimDeleteContractObject';
import type { ItemTableContractObject } from './hooks/useBimItemsContractStructureTable';
import {
    getContractTreeItem,
    updateContractTreeItem,
    useBimItemsContractStructureTable,
} from './hooks/useBimItemsContractStructureTable';
import { useBimMoveContractObject } from './hooks/useBimMoveContractObject';
import { useBimUploadContractObjects } from './hooks/useBimUploadContractObjects';

import styles from './BimContractStructure.module.css';

const hierarchyColumnTitle = 'Объект / подобъект';

const getInformer = (o: BimContractObjectData) => ({
    items: [{ icon: <Badge size="s" status="success" label="Д" /> }],
    treeItemId: o.id,
});

export const BimContractStructure = () => {
    const { id: projectId, methodology } = useProjectParams();

    const dispatch = useDispatch();
    const { refreshProject } = useProjectData(projectId, false);

    const [activeTab, setActiveTab] = useState<ITabItem<number>>();
    const [isOpenCreateObject, setIsOpenCreateObject] = useState<boolean>(false);
    const [isOpenCreateSubObject, setIsOpenCreateSubObject] = useState<boolean>(false);
    const [contractObject, setContractObject] = useState<ItemTableContractObject | undefined>(undefined);
    const [parentContractObject, setParentContractObject] = useState<ItemTableContractObject | undefined>(undefined);
    const [isParentWithGuaranteedVolume, setIsParentWithGuaranteedVolume] = useState(false);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);
    const [contractStructureStyle, setContractStructureStyle] = useState<React.CSSProperties>();
    const [hasGuaranteedVolume, setHasGuaranteedVolume] = useState<string>();
    const [informers, setInformers] = useState<ITableLikeTreeItemNotification[]>([]);
    const [areAllExpanded, setAreAllExpanded] = useState(true);
    const [rowsCount, setRowsCount] = useState(0);

    const contextRef = useRef<ITableLikeTreeContext | undefined>();
    const tableRef = useRef<HTMLDivElement>(null);

    const { contractBim: contractBimRules } = useAccess();

    const { projectData, projectDataStatus } = useProjectData(projectId);

    const { isFrameContract, workOrderStatus } = useGetBimWorkOrderDataFromProject({});

    const contractId = projectData?.contractId;
    const contractHasWorkOrders = projectData?.contractNavigation?.data?.contractNavData?.hasWorkOrders;

    const { bimContractData, bimContractDataStatus, makeResetBimContract } = useBimContract(contractId);

    const seasons = bimContractData?.seasons;
    const isContractTypeNormal = bimContractData?.contractType === ProjectContractTypeEnum.Normal;

    const isContractCompletedOrDelayed = useMemo(
        () =>
            [BimContractStatusEnum.Completed, BimContractStatusEnum.Delayed].includes(
                bimContractData?.status as BimContractStatusEnum,
            ),
        [bimContractData?.status],
    );

    const { bimContractObjectsData, bimContractObjectsStatus, bimContractObjectsMakeRequest, resetBimContractObjects } =
        useBimContractObjects({ contractId, constructionTypeId: activeTab?.id });

    const onDeleteSuccess = useCallback(() => {
        if (contractObject) {
            contextRef?.current?.delete(contractObject);

            setContractObject(undefined);
            setInformers((prev) => prev.filter((i) => i.treeItemId !== contractObject.id));
            bimContractObjectsMakeRequest();
            refreshProject();
        }
    }, [bimContractObjectsMakeRequest, contractObject, refreshProject]);

    const { bimDeleteContractObjectRequest } = useBimDeleteContractObject({
        onSuccess: onDeleteSuccess,
    });

    const isBimContractObjectsData = !!bimContractObjectsData?.length;

    const {
        bimImportContractObjectsData,
        bimImportContractObjectsStatus,
        onSetFileDownloadObjects,
        isOpenImportContractObjectsConfirmationDialog,
        onActionImportContractObjectsConfirmationDialog,
        onCloseImportContractObjectsConfirmationDialog,
        resetUploadData,
    } = useBimUploadContractObjects({
        contractId,
        bimContractObjectsData: bimContractObjectsData as BimContractObjectData[],
        isDataExists: isBimContractObjectsData,
    });

    const isLoadingPage =
        projectDataStatus === RequestStatus.FETCHING ||
        bimContractObjectsStatus === RequestStatus.FETCHING ||
        bimContractDataStatus === RequestStatus.FETCHING;

    const isLoadingImportFile = bimImportContractObjectsStatus === RequestStatus.FETCHING;

    const downloadObjectsTitle = useMemo(
        () => (isBimContractObjectsData ? 'Загрузка нового файла' : 'Загрузка структуры договора'),
        [isBimContractObjectsData],
    );

    const itemsContractStructureTable = useBimItemsContractStructureTable(
        bimContractObjectsData as BimContractObjectData[],
    );

    const itemsContractStructureTableFiltered = useMemo(
        () =>
            hasGuaranteedVolume
                ? itemsContractStructureTable?.filter((item) => item.hasGuaranteedVolume === hasGuaranteedVolume)
                : itemsContractStructureTable,
        [hasGuaranteedVolume, itemsContractStructureTable],
    );

    const constructionTypesTabsItems = useMemo(
        () =>
            bimContractData?.constructionTypes
                ?.map((type) => ({ id: type.id, label: type.name }))
                .sort(({ label: a }, { label: b }) => a.localeCompare(b)),
        [bimContractData?.constructionTypes],
    );

    useEffect(() => {
        if (constructionTypesTabsItems?.length) {
            setActiveTab(constructionTypesTabsItems[0]);
        }
    }, [constructionTypesTabsItems]);

    useEffect(
        () => () => {
            makeResetBimContract(null);
            resetBimContractObjects(null);
        },
        [makeResetBimContract, resetBimContractObjects],
    );

    const onResize = useCallback(() => {
        setContractStructureStyle({
            '--others-height':
                isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                `${(tableRef.current?.getBoundingClientRect?.()?.top as number) + 30}px`,
        } as React.CSSProperties);
    }, []);

    useEffect(() => {
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [onResize]);

    useEffect(() => {
        if (!isLoadingPage && isBimContractObjectsData) {
            runOnRefExists(tableRef, onResize);
        }
    }, [isBimContractObjectsData, isLoadingPage, onResize]);

    useEffect(() => {
        if (!isFrameContract && workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            return;
        }

        setInformers(
            bimContractObjectsData
                ?.filter((o) => o.status === BimContractObjectStatusEnum.NewRecord)
                .map(getInformer) || [],
        );
    }, [bimContractObjectsData, isFrameContract, workOrderStatus]);

    const seasonColumns: TableColumn<any>[] = useMemo(() => {
        if (!seasons || !seasons.length) {
            return [];
        }

        const sortedSeasons = [...seasons].sort(sortByYear);

        const columnsSeason: TableColumn<any>[] = sortedSeasons.map((season) => ({
            title: season.year,
            accessor: `season_${season.year}`,
            align: 'center',
            width: 100,
        }));

        return [
            {
                title: 'Гарантированный объем',
                align: 'center',
                accessor: 'hasGuaranteedVolume',
                width: 200,
            },
            {
                title: 'Распределение итогов по сезонам',
                align: 'center',
                columns: columnsSeason,
            },
        ];
    }, [seasons]);

    const filters = useBimContractStructureFilters();

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectData?.uniqueId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [methodology, projectData?.uniqueId, projectData?.name],
    );

    const onFiltersUpdated = useCallback((currentFilters: SelectedFilters) => {
        setHasGuaranteedVolume(currentFilters?.hasGuaranteedVolume?.value?.value);
    }, []);

    const onTabChange = useCallback(({ value }) => {
        setActiveTab(value);
    }, []);

    const onOpenCreateObject = useCallback(() => {
        setIsOpenCreateObject(true);
    }, []);

    const onCloseCreateObject = useCallback(() => {
        setIsOpenCreateObject(false);
        setContractObject(undefined);
    }, []);

    const onOpenCreateSubObject = useCallback((_: any, { item }: { item: ItemTableContractObject }) => {
        setParentContractObject(item);
        setIsOpenCreateSubObject(true);
        setIsParentWithGuaranteedVolume(item?.hasGuaranteedVolume === 'Да');
    }, []);

    const onCloseCreateSubObject = useCallback(() => {
        setIsOpenCreateSubObject(false);
        setContractObject(undefined);
        setParentContractObject(undefined);
    }, []);

    const onOpenDeleteObject = useCallback((_: any, context?: ITableLikeTreeMenuOperationContext) => {
        if (context?.item) {
            setIsOpenWarningDialog(true);
            setContractObject(context?.item as ItemTableContractObject);
        }
    }, []);

    const onSubmitWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
        setContractObject(undefined);
        dispatch(bimDeleteContractObjectRequest({ contractObjectId: contractObject?.id }));
    }, [bimDeleteContractObjectRequest, contractObject, dispatch]);

    const onCloseWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const onChangeObject = useCallback((_: any, { item }: { item: ItemTableContractObject }) => {
        if (!item.parentId) {
            setIsOpenCreateObject(true);
            setContractObject(item);
        } else {
            setIsOpenCreateSubObject(true);
            setContractObject(item);
        }
    }, []);

    const kebabMenu = useMemo(
        () => [
            {
                label: 'Редактировать',
                itemId: 1,
                groupId: 1,
                action: onChangeObject,
                disabled: isContractCompletedOrDelayed || contractHasWorkOrders || !contractBimRules?.canEdit,
            },
            {
                label: 'Создать подобъект',
                itemId: 2,
                groupId: 1,
                action: onOpenCreateSubObject,
                disabled:
                    isContractCompletedOrDelayed ||
                    contractHasWorkOrders ||
                    !contractBimRules?.canCreateContractStructure,
            },
            {
                label: 'Удалить',
                action: onOpenDeleteObject,
                itemId: 3,
                groupId: 2,
                disabled: isContractCompletedOrDelayed || contractHasWorkOrders || !contractBimRules?.canDelete,
            },
        ],
        [
            onChangeObject,
            isContractCompletedOrDelayed,
            contractHasWorkOrders,
            contractBimRules?.canEdit,
            contractBimRules?.canCreateContractStructure,
            contractBimRules?.canDelete,
            onOpenCreateSubObject,
            onOpenDeleteObject,
        ],
    );

    const onMoveToContractObjects = useCallback(() => {
        resetUploadData();
        bimContractObjectsMakeRequest();
        refreshProject();
    }, [bimContractObjectsMakeRequest, refreshProject, resetUploadData]);

    const getTagLabel = useCallback((id: string, name: string, filterValue: any) => {
        const val = id === 'hasGuaranteedVolume' ? filterValue?.value : filterValue;

        return `${name}: '${val || ''}'`;
    }, []);

    const onDrop = useBimMoveContractObject();

    const onCreateOrEditObjectSuccess = useCallback(
        (item: BimContractObjectData) => {
            if (contractObject) {
                contextRef?.current?.replace(updateContractTreeItem(contractObject, item));
            } else if (contextRef?.current) {
                contextRef?.current?.addChild(getContractTreeItem(item));
            } else {
                bimContractObjectsMakeRequest();
            }

            if (item.status === BimContractObjectStatusEnum.NewRecord) {
                setInformers((prev) => [...prev, getInformer(item)]);
            }

            setContractObject(undefined);
        },
        [bimContractObjectsMakeRequest, contractObject],
    );

    const onCreateOrEditSubObjectSuccess = useCallback(
        (item: BimContractObjectData) => {
            if (contractObject) {
                contextRef?.current?.replace(updateContractTreeItem(contractObject, item));
            } else {
                contextRef?.current?.addChild(getContractTreeItem(item), parentContractObject);
            }

            if (item.status === BimContractObjectStatusEnum.NewRecord) {
                setInformers((prev) => [...prev, getInformer(item)]);
            }

            setContractObject(undefined);
            setParentContractObject(undefined);
            setIsOpenCreateSubObject(true);
            setIsParentWithGuaranteedVolume(false);
        },
        [contractObject, parentContractObject],
    );

    return (
        <ProjectPageLayout
            title="Структура договора"
            breadCrumbsItems={breadCrumbsItems}
            style={{ height: 'calc(100% - 60px)' }}
        >
            <Layout direction="column">
                <>
                    <Layout className={cn(styles.wrapper_toolbar, cnMixSpace({ mB: 'xl' }))}>
                        <FilesSelectorButton
                            label="Загрузить структуру"
                            view="secondary"
                            size="s"
                            accept=".xlsx"
                            isLoading={isLoadingPage || isLoadingImportFile}
                            isDisabled={
                                isLoadingPage ||
                                isLoadingImportFile ||
                                contractHasWorkOrders ||
                                isContractCompletedOrDelayed ||
                                !contractBimRules?.canCreateContractStructure
                            }
                            onDropFiles={onSetFileDownloadObjects}
                        />
                    </Layout>
                    {constructionTypesTabsItems && (
                        <Layout className={cnMixSpace({ mB: 'xl' })}>
                            <Tabs
                                value={activeTab}
                                onChange={onTabChange}
                                items={constructionTypesTabsItems || []}
                                getItemLabel={(item: { label: string | number }) => item.label}
                                view="bordered"
                            />
                        </Layout>
                    )}
                </>
            </Layout>
            {isLoadingPage ? (
                <Loader />
            ) : (
                <div id="contractStructure" style={contractStructureStyle}>
                    {!isLoadingPage && isBimContractObjectsData && (
                        <TotalCount totalCount={rowsCount} className={cnMixSpace({ mV: 'm' })} />
                    )}
                    <Layout className={styles.topBar}>
                        <Button
                            label="Развернуть все"
                            onClick={() => contextRef?.current?.switchExpandAll?.(true)}
                            disabled={!isBimContractObjectsData || areAllExpanded}
                            view="clear"
                            size="s"
                        />
                        <Button
                            label="Свернуть все"
                            onClick={() => contextRef?.current?.switchExpandAll?.(false)}
                            disabled={!isBimContractObjectsData || !areAllExpanded}
                            view="clear"
                            size="s"
                        />
                        <Button
                            style={{ marginLeft: 'auto' }}
                            label="Создать объект"
                            size="s"
                            onClick={onOpenCreateObject}
                            loading={isLoadingPage || isLoadingImportFile}
                            view="secondary"
                            disabled={
                                isContractCompletedOrDelayed ||
                                contractHasWorkOrders ||
                                !contractBimRules?.canCreateContractStructure
                            }
                        />
                    </Layout>
                    {!isBimContractObjectsData && (
                        <Informer
                            className={cn(cnMixSpace({ mT: 'm' }), styles.informer)}
                            view="filled"
                            status="system"
                            label="Для отображения данных загрузите из шаблона или создайте структуру договора вручную"
                            size="s"
                        />
                    )}
                    {!isLoadingPage && isBimContractObjectsData ? (
                        <div ref={tableRef} className={styles.bimContractStructureContainer}>
                            <TableLikeTree
                                hierarchyColumnTitle={hierarchyColumnTitle}
                                hierarchyColumnFilterTitle={hierarchyColumnTitle}
                                items={itemsContractStructureTableFiltered}
                                menuItems={kebabMenu}
                                setContextMenuInHierarchyColumn
                                additionalColumns={seasonColumns}
                                className={cnMixSpace({ mT: 'm' })}
                                isItemMovableOnlyInsideRootItemBrunch
                                isRootItemUnableToAppend
                                filters={filters}
                                isFilterEnabled
                                onFiltersUpdated={onFiltersUpdated}
                                getTagLabel={getTagLabel}
                                informers={informers}
                                menuColumnWidth={36}
                                menuColumnAlign="center"
                                onDrop={onDrop}
                                contextRef={contextRef}
                                deleteWithoutConfirmation
                                onExpandAllSwitch={setAreAllExpanded}
                                onRowsCountChanged={setRowsCount}
                            />
                        </div>
                    ) : null}
                </div>
            )}

            {isOpenCreateObject && (
                <BimCreateStructureObject
                    contractId={contractId}
                    constructionTypeId={activeTab?.id}
                    seasons={seasons}
                    contractObject={contractObject}
                    onSuccess={onCreateOrEditObjectSuccess}
                    onClose={onCloseCreateObject}
                    isContractTypeNormal={isContractTypeNormal}
                    refreshProject={refreshProject}
                    bimContractObjectsMakeRequest={bimContractObjectsMakeRequest}
                    isUseContract
                />
            )}
            {isOpenCreateSubObject && (
                <BimCreateStructureSubObject
                    contractObject={contractObject}
                    parentContractObject={parentContractObject}
                    onClose={onCloseCreateSubObject}
                    onSuccess={onCreateOrEditSubObjectSuccess}
                    isContractTypeNormal={isContractTypeNormal}
                    isParentWithGuaranteedVolume={isParentWithGuaranteedVolume}
                    isUseContract
                />
            )}
            <ConfirmationDialog
                textHeader={`Удаление ${!contractObject?.parentId ? 'объекта' : 'подобъекта'}`}
                textConfirmation={`Вы уверены, что хотите удалить ${
                    !contractObject?.parentId ? 'объект? Все дочерние элементы будут удалены' : 'подобъект?'
                }`}
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                onSubmit={onSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={onCloseWarningDialog}
            />
            {bimImportContractObjectsStatus === RequestStatus.SUCCESS && bimImportContractObjectsData?.result && (
                <UploadStatus
                    isOpened
                    handleCloseModal={onMoveToContractObjects}
                    statusText="Проверка завершена. Ошибок не обнаружено."
                    titleText={downloadObjectsTitle}
                    status="success"
                >
                    <Button label="Перейти к структуре" onClick={onMoveToContractObjects} />
                </UploadStatus>
            )}
            <ConfirmationDialog
                textHeader={downloadObjectsTitle}
                textConfirmation="При загрузке нового файла все данные по структуре договора будут удалены"
                cancelButtonText="Отменить"
                confirmButtonText="Продолжить"
                isOpened={isOpenImportContractObjectsConfirmationDialog}
                onSubmit={onActionImportContractObjectsConfirmationDialog}
                onClose={onCloseImportContractObjectsConfirmationDialog}
            />
        </ProjectPageLayout>
    );
};

BimContractStructure.displayName = 'BimContractStructure';
